// WebSocket-relayed voice chat for Carrom. Cloned 1:1 from
// games/ludo/wsVoice.ts — see VOICE_CHAT_COOKBOOK.md §3 for the
// rules (especially Rule 1: each game has its OWN wsVoice with its
// OWN __<game>MicStream key — DO NOT share with Ludo).
//
// Cookbook deltas vs the Ludo file:
//   • `Color` (Ludo) → `Seat` (Carrom — 'p1'..'p4').
//   • `window.__ludoMicStream` → `window.__carromMicStream` (Rule 1).
//   • Log prefix `[wsVoice]` → `[carrom/voice]` (Rule 10).
//   • Adds a `sentFrames` diagnostic counter (Rule 10) so the
//     browser console prints "[carrom/voice] sent N frames" every
//     ~2 s of active mic — invaluable when debugging dropped audio.

import type { Seat } from './Board';

// 8 kHz mono Int16 PCM in 20 ms frames. 160 samples per frame, 320
// bytes on the wire. Telephone-grade quality, tiny packet size.
const WIRE_SAMPLE_RATE = 8000;
const FRAME_SAMPLES = 160;

type SendFn = (msg: { type: 'voice_chunk'; pcm: string }) => void;

export class WSVoice {
  private ownSeat: Seat;
  private send: SendFn;

  private micStream: MediaStream | null = null;
  private captureCtx: AudioContext | null = null;
  private captureNode: ScriptProcessorNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  // Buffer samples from the browser's native sample rate (usually
  // 48 kHz) until we've collected enough to downsample into one
  // 160-sample frame.
  private sampleQueue: Float32Array[] = [];
  private sampleQueueLen = 0;
  private browserSampleRate = 48000;

  private playbackCtx: AudioContext | null = null;
  // Per-remote scheduler. nextStartTime is the AudioContext timestamp
  // at which the NEXT chunk should start playing (Rule 8 — keeps
  // frames gapless).
  private nextStartTime = new Map<number, number>();

  private muted = false;
  private listenMuted = false;
  // Cookbook Rule 10 — bump every shipped frame, log every 100.
  private sentFrames = 0;
  private recvFrames = 0;
  // Hard "we are done" flag set by stop(). All callbacks
  // (onaudioprocess, drainToFrames, onRemoteChunk) bail
  // immediately when this is true, so a callback already in
  // flight at the moment stop() runs can't keep ferrying frames
  // after the AudioContext is closed. Without this, a stale tab
  // could keep emitting silent frames forever — the server logs
  // them as "[carrom/voice] no room for user N" every 4 s.
  private stopped = false;

  constructor(ownSeat: Seat, send: SendFn) {
    this.ownSeat = ownSeat;
    this.send = send;
  }

  // Acquire the mic. Call inside a user-gesture click handler if
  // possible (Rule 2). Stash on window.__carromMicStream so start()
  // can pick it up without a second getUserMedia call (Rule 3).
  static async prewarmMic(): Promise<MediaStream | null> {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      (window as any).__carromMicStream = s;
      return s;
    } catch (e) {
      console.warn('[carrom/voice] prewarmMic denied', e);
      return null;
    }
  }

  async start(): Promise<boolean> {
    try {
      const warm = (window as any).__carromMicStream as MediaStream | undefined;
      if (warm && warm.getAudioTracks().some((t) => t.readyState === 'live')) {
        this.micStream = warm;
        (window as any).__carromMicStream = null;
      } else {
        this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      }
    } catch (e) {
      console.warn('[carrom/voice] mic denied', e);
      return false;
    }

    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    this.captureCtx = new Ctx();
    this.browserSampleRate = this.captureCtx.sampleRate;
    this.sourceNode = this.captureCtx.createMediaStreamSource(this.micStream);
    // ScriptProcessorNode is deprecated but universally supported and
    // perfectly fine for 8 kHz voice. Buffer 2048 @ 48 kHz = 43 ms.
    this.captureNode = this.captureCtx.createScriptProcessor(2048, 1, 1);
    this.sourceNode.connect(this.captureNode);
    // Cookbook Rule 6: connect to destination via a zero-gain node so
    // the processor keeps firing onaudioprocess but the user never
    // hears their own mic.
    const silent = this.captureCtx.createGain();
    silent.gain.value = 0;
    this.captureNode.connect(silent);
    silent.connect(this.captureCtx.destination);

    this.captureNode.onaudioprocess = (ev) => {
      if (this.stopped) return;
      if (this.muted)   return;
      const input = ev.inputBuffer.getChannelData(0);
      const copy = new Float32Array(input.length);
      copy.set(input);
      this.sampleQueue.push(copy);
      this.sampleQueueLen += copy.length;
      this.drainToFrames();
    };

    // Playback context — separate from capture so their sample rates
    // don't have to match. Kept at browser default for best fidelity.
    this.playbackCtx = new Ctx();
    if (this.playbackCtx.state === 'suspended') {
      try { await this.playbackCtx.resume(); } catch {}
    }
    console.info('[carrom/voice] started, browserRate=', this.browserSampleRate, 'wire=', WIRE_SAMPLE_RATE);
    return true;
  }

  // Drain the queue: whenever we have enough raw samples to produce
  // one 160-sample frame at WIRE_SAMPLE_RATE, do it and ship the frame.
  private drainToFrames() {
    if (this.stopped) return;
    const ratio = this.browserSampleRate / WIRE_SAMPLE_RATE;
    const rawNeeded = Math.ceil(FRAME_SAMPLES * ratio);
    while (this.sampleQueueLen >= rawNeeded) {
      const raw = new Float32Array(rawNeeded);
      let filled = 0;
      while (filled < rawNeeded && this.sampleQueue.length) {
        const head = this.sampleQueue[0];
        const take = Math.min(rawNeeded - filled, head.length);
        raw.set(head.subarray(0, take), filled);
        filled += take;
        if (take === head.length) this.sampleQueue.shift();
        else this.sampleQueue[0] = head.subarray(take);
      }
      this.sampleQueueLen -= rawNeeded;

      // Downsample by stride — cheap, fine for voice.
      const frame = new Int16Array(FRAME_SAMPLES);
      for (let i = 0; i < FRAME_SAMPLES; i++) {
        const src = raw[Math.floor(i * ratio)] || 0;
        const clamped = Math.max(-1, Math.min(1, src));
        frame[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      }

      const b64 = int16ToBase64(frame);
      try { this.send({ type: 'voice_chunk', pcm: b64 }); } catch {}
      // Cookbook Rule 10 — every 100 frames (~2 s of mic) print one
      // line so a "voice not working" report can be triaged at a
      // glance from the browser console.
      this.sentFrames++;
      if (this.sentFrames % 100 === 0) {
        console.info('[carrom/voice] sent', this.sentFrames, 'frames');
      }
    }
  }

  // Called by Page.tsx when a voice_chunk message arrives from a
  // remote seat. The server tags `from` with the sender's seat NUMBER
  // (1, 2, …); our own-seat compare uses `seatKeyToNum` semantics
  // inline so this stays self-contained.
  onRemoteChunk(from: number, b64: string) {
    if (this.stopped) return;
    if (from === Number(this.ownSeat.slice(1))) return;
    if (this.listenMuted) return;
    if (!this.playbackCtx) return;
    const frame = base64ToInt16(b64);
    if (!frame || frame.length === 0) return;
    const f32 = new Float32Array(frame.length);
    for (let i = 0; i < frame.length; i++) f32[i] = frame[i] / 0x8000;
    const buf = this.playbackCtx.createBuffer(1, frame.length, WIRE_SAMPLE_RATE);
    buf.copyToChannel(f32, 0);
    const src = this.playbackCtx.createBufferSource();
    src.buffer = buf;
    src.connect(this.playbackCtx.destination);

    // Cookbook Rule 8 — schedule at nextStartTime, not now. Network
    // stall? Reset so we recover instead of falling further behind.
    const now = this.playbackCtx.currentTime;
    let at = this.nextStartTime.get(from) ?? now + 0.05;
    if (at < now) at = now + 0.02;
    src.start(at);
    this.nextStartTime.set(from, at + buf.duration);

    this.recvFrames++;
    if (this.recvFrames % 100 === 0) {
      console.info('[carrom/voice] received', this.recvFrames, 'frames');
    }
  }

  setMuted(m: boolean) { this.muted = m; }
  isMuted() { return this.muted; }
  setListenMuted(m: boolean) { this.listenMuted = m; }
  isListenMuted() { return this.listenMuted; }

  hasLocalStream() {
    return !!this.micStream && this.micStream.getAudioTracks().some((t) => t.readyState === 'live');
  }

  // Fallback for the mic-toggle button — when the first start()
  // failed (permission denied) and the user retried with an explicit
  // gesture. Re-runs the full capture setup with the freshly-granted
  // stream. Cookbook Rule 4: NEVER call attachLocalStream + start
  // back-to-back from startVoice; only one path may run at a time.
  async attachLocalStream(stream: MediaStream) {
    (window as any).__carromMicStream = stream;
    await this.start();
  }

  // Cookbook Rule 7 — Safari can leave the playback context suspended
  // after a mute toggle. Nudge it back to life on listen-unmute.
  kickAudioPlayback() {
    if (this.playbackCtx && this.playbackCtx.state === 'suspended') {
      this.playbackCtx.resume().catch(() => {});
    }
  }

  stop() {
    if (this.stopped) return;          // idempotent
    // Set the flag FIRST so any callback already in flight (a
    // pending onaudioprocess from before captureNode.disconnect
    // landed) bails immediately instead of producing one more
    // straggler frame.
    this.stopped = true;
    // Explicitly null the captureNode's process callback so even
    // if the browser fires one more after disconnect (some Safari
    // versions do), it's a no-op rather than referencing a dead
    // AudioContext.
    if (this.captureNode) {
      try { this.captureNode.onaudioprocess = null; } catch {}
    }
    try { this.captureNode?.disconnect(); } catch {}
    try { this.sourceNode?.disconnect(); } catch {}
    try { this.captureCtx?.close(); } catch {}
    try { this.playbackCtx?.close(); } catch {}
    this.captureCtx = null;
    this.playbackCtx = null;
    this.captureNode = null;
    this.sourceNode = null;
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    this.sampleQueue = [];
    this.sampleQueueLen = 0;
    this.nextStartTime.clear();
  }
}

// ─── tiny base64 helpers for Int16 PCM frames ──────────────────────
// 320 bytes of Int16 → ~428 chars of base64. Same on-the-wire format
// every game uses, so the server's relay is game-agnostic.
function int16ToBase64(arr: Int16Array): string {
  const bytes = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
  let s = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + CHUNK) as unknown as number[],
    );
  }
  return btoa(s);
}

function base64ToInt16(b64: string): Int16Array | null {
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    if (bytes.length % 2 !== 0) return null;
    return new Int16Array(bytes.buffer, bytes.byteOffset, bytes.length / 2);
  } catch {
    return null;
  }
}
